import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarDays, MessageCircle, TrendingUp, GraduationCap } from "lucide-react";
import PlayerTrainingSessions from "@/components/training/PlayerTrainingSessions";
import ParentDashboard from "@/components/training/ParentDashboard";
import GroupChat from "@/components/training/GroupChat";
import PlayerFeedbackHistory from "@/components/training/PlayerFeedbackHistory";
import PlayerRadarChart from "@/components/training/PlayerRadarChart";
import CoachBooking from "@/components/training/CoachBooking";
import TrainingSidebar from "@/components/training/TrainingSidebar";
import ResponsiveLayout from "@/components/layout/ResponsiveLayout";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Training() {
  const { t } = useTranslation("activities");
  const { user } = useAuth();
  const { roles } = useUserRole();
  const isParent = roles.includes("parent");
  const [tab, setTab] = useState("sessions");
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  // Fetch groups the user belongs to
  const { data: myGroups = [] } = useQuery({
    queryKey: ["my-groups-chat", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("group_members")
        .select("group_id, training_groups(id, name)")
        .eq(isParent ? "parent_id" : "player_id", user!.id)
        .eq("status", "active");
      return (data ?? [])
        .filter(d => d.training_groups)
        .map(d => ({
          id: (d.training_groups as any).id as string,
          name: (d.training_groups as any).name as string,
        }));
    },
    enabled: !!user,
  });

  // Auto-select first group
  const activeGroupId = selectedGroupId ?? (myGroups.length > 0 ? myGroups[0].id : null);
  const activeGroup = myGroups.find(g => g.id === activeGroupId);

  const tabsContent = (
    <Tabs value={tab} onValueChange={setTab}>
      <TabsList>
        <TabsTrigger value="sessions" className="gap-1">
          <CalendarDays className="h-4 w-4" /> {t("training.tabs.sessions")}
        </TabsTrigger>
        {!isParent && (
          <TabsTrigger value="development" className="gap-1">
            <TrendingUp className="h-4 w-4" /> {t("training.tabs.development")}
          </TabsTrigger>
        )}
        {myGroups.length > 0 && (
          <TabsTrigger value="chat" className="gap-1">
            <MessageCircle className="h-4 w-4" /> {t("common:nav.chat")}
          </TabsTrigger>
        )}
        <TabsTrigger value="coach" className="gap-1">
          <GraduationCap className="h-4 w-4" /> {t("training.coach")}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="sessions">
        {isParent ? <ParentDashboard /> : <PlayerTrainingSessions />}
      </TabsContent>
      {!isParent && user && (
        <TabsContent value="development" className="space-y-4">
          <PlayerRadarChart playerId={user.id} />
          <h3 className="text-sm font-semibold">{t("training.feedbackHistory")}</h3>
          <PlayerFeedbackHistory playerId={user.id} />
        </TabsContent>
      )}
      <TabsContent value="chat">
        {myGroups.length > 1 && (
          <div className="mb-3">
            <Select value={activeGroupId ?? ""} onValueChange={setSelectedGroupId}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder={t("training.selectGroup")} />
              </SelectTrigger>
              <SelectContent>
                {myGroups.map(g => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {activeGroupId && activeGroup && (
          <GroupChat groupId={activeGroupId} groupName={activeGroup.name} />
        )}
      </TabsContent>
      <TabsContent value="coach">
        <CoachBooking />
      </TabsContent>
    </Tabs>
  );

  return (
    <div className="space-y-4 pb-20 lg:pb-6">
      <h1 className="text-2xl font-bold">{isParent ? t("training.childTrainingTitle") : t("training.myTrainingTitle")}</h1>
      <ResponsiveLayout
        main={tabsContent}
        sidebar={<TrainingSidebar />}
        sidebarWidth="w-72"
      />
    </div>
  );
}
